import { Controller } from "@hotwired/stimulus";

// Shared global counter for open dialogs (modals and slideovers)
// This ensures proper scrollbar compensation when mixing modals and slideovers
if (!window.__openDialogCount) {
  window.__openDialogCount = 0;
}

// Reset dialog count on page navigation to prevent desync issues
// This handles cases where users navigate back/forward while dialogs are open
if (!window.__dialogCountResetBound) {
  window.__dialogCountResetBound = true;

  const resetDialogCount = () => {
    // Only reset if no dialogs are actually open
    const openDialogs = document.querySelectorAll("dialog[open]");
    if (openDialogs.length === 0) {
      window.__openDialogCount = 0;
      document.documentElement.style.removeProperty("--scrollbar-compensation");
      document.body.classList.remove("modal-open", "slideover-open");
    } else {
      // Sync count with actual open dialogs
      window.__openDialogCount = openDialogs.length;
    }
  };

  document.addEventListener("turbo:before-cache", resetDialogCount);
  document.addEventListener("turbo:load", resetDialogCount);
}

export default class extends Controller {
  static targets = ["dialog", "template"];
  static values = {
    open: { type: Boolean, default: false }, // Whether the slideover is open
    lazyLoad: { type: Boolean, default: false }, // Whether to lazy load the slideover content
    turboFrameSrc: { type: String, default: "" }, // URL for the turbo frame
    autoFocus: { type: Boolean, default: false }, // True: focus first focusable element on open. False: don't focus on anything by default. (if an element has autofocus="true", it will focus on it whether this option is true or false)
    titleContext: String, // Optional: Context for page title (e.g., "Edit Location")
  };

  connect() {
    // Initialize state
    this.contentLoaded = false;
    this.isOpen = false; // Track if this specific slideover is open
    this.originalTitle = null; // Store original title for restoration

    // Initialize focus trapping
    this.focusableElements = [];
    this.firstFocusableElement = null;
    this.lastFocusableElement = null;

    // Check if dialog is already open (from previous page state) and close it
    if (this.dialogTarget.open) {
      // Mark as open temporarily to allow cleanup
      this.isOpen = true;
      this.dialogTarget.close();
      this.cleanupScrollbarCompensation();
    }

    // Now handle the intended open state
    if (this.openValue) this.open();

    // Allow dynamic content to open/close via custom events (like modal pattern)
    this.boundExternalOpen = (e) => {
      const src = e?.detail?.src || "";
      const reset = e?.detail?.reset ?? true;

      if (src) {
        // allow per-click URLs
        this.turboFrameSrcValue = src;
        this.lazyLoadValue = true;
        // force reload when URL changes or when caller requests reset
        if (reset || this._lastSrc !== src) this.contentLoaded = false;
        this._lastSrc = src;
      }

      this.open();
    };
    this.boundExternalClose = (e) => {
      // optional if you have nested slideovers:
      e.stopPropagation();
      this.close();
    };

    // listen on the element for custom events
    this.element.addEventListener("slideover:open", this.boundExternalOpen);
    this.element.addEventListener("slideover:close", this.boundExternalClose);

    // Set up event listeners
    this.boundBeforeCache = this.beforeCache.bind(this);
    this.boundBeforeVisit = this.beforeVisit.bind(this);
    document.addEventListener("turbo:before-cache", this.boundBeforeCache);
    document.addEventListener("turbo:before-visit", this.boundBeforeVisit);

    // Add event listener for when dialog is closed by any means (including Escape key)
    this.dialogTarget.addEventListener(
      "close",
      this.handleDialogClose.bind(this),
    );

    // Additional keydown listener for focus trapping
    this.boundHandleKeydown = this.handleKeydown.bind(this);
    this.dialogTarget.addEventListener("keydown", this.boundHandleKeydown);
  }

  disconnect() {
    // If this slideover was open when disconnecting, clean up scrollbar compensation and restore title
    if (this.isOpen) {
      this.cleanupScrollbarCompensation();
      this.restoreTitle();
    }

    // Remove custom event listeners
    this.element.removeEventListener("slideover:open", this.boundExternalOpen);
    this.element.removeEventListener("slideover:close", this.boundExternalClose);

    document.removeEventListener("turbo:before-cache", this.boundBeforeCache);
    document.removeEventListener("turbo:before-visit", this.boundBeforeVisit);
    this.dialogTarget.removeEventListener(
      "close",
      this.handleDialogClose.bind(this),
    );
    this.dialogTarget.removeEventListener("keydown", this.boundHandleKeydown);
  }

  async open() {
    // If already open, don't do anything
    if (this.isOpen) return;

    // If lazy loading is enabled and content hasn't been loaded yet, load it now
    if (this.lazyLoadValue && !this.contentLoaded) {
      await this.#loadTemplateContent();
      this.contentLoaded = true;
    }

    // Increment the counter and mark this slideover as open
    window.__openDialogCount++;
    this.isOpen = true;

    // Only apply scrollbar compensation if this is the first dialog
    if (window.__openDialogCount === 1) {
      const scrollbarWidth = this.getScrollbarWidth();
      if (scrollbarWidth > 0) {
        // Set CSS custom property for scrollbar width
        document.documentElement.style.setProperty(
          "--scrollbar-compensation",
          `${scrollbarWidth}px`,
        );
        // Add class to body to trigger compensation
        document.body.classList.add("slideover-open");
      }
    }

    this.dialogTarget.showModal();

    // Set up focus trapping
    this.setupFocusTrapping();

    // Update page title with context
    this.updateTitle();
  }

  close() {
    // If not open, don't do anything
    if (!this.isOpen) return;

    // Restore title before closing
    this.restoreTitle();

    this.dialogTarget.setAttribute("closing", "");

    Promise.all(
      this.dialogTarget.getAnimations().map((animation) => animation.finished),
    ).then(() => {
      this.dialogTarget.removeAttribute("closing");
      this.dialogTarget.close();

      // The handleDialogClose method will handle the cleanup when the dialog actually closes
      // This prevents double cleanup and ensures proper timing
    });
  }

  backdropClose(event) {
    // Only close if clicking directly on the dialog element (the backdrop)
    if (event.target.nodeName === "DIALOG") {
      // Stop propagation to prevent closing parent dialogs
      event.stopPropagation();

      this.close();
    }
  }

  show() {
    this.open();
  }

  hide(event) {
    if (event) event.preventDefault();
    this.close();
  }

  beforeCache() {
    // Close immediately without animation during navigation
    if (this.isOpen) {
      this.dialogTarget.removeAttribute("closing");
      this.dialogTarget.close();
      this.cleanupScrollbarCompensation();
      this.restoreTitle();
    }
  }

  beforeVisit() {
    // Close immediately without animation during navigation
    if (this.isOpen) {
      this.dialogTarget.removeAttribute("closing");
      this.dialogTarget.close();
      this.cleanupScrollbarCompensation();
      this.restoreTitle();
    }
  }

  // Calculate actual scrollbar width
  getScrollbarWidth() {
    // Create a temporary div to measure scrollbar width
    const outer = document.createElement("div");
    outer.style.visibility = "hidden";
    outer.style.overflow = "scroll";
    outer.style.msOverflowStyle = "scrollbar"; // Force scrollbars on IE/Edge
    document.body.appendChild(outer);

    const inner = document.createElement("div");
    outer.appendChild(inner);

    const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
    outer.parentNode.removeChild(outer);

    return scrollbarWidth;
  }

  async #loadTemplateContent() {
    // Find the container in the dialog to append content to
    const container =
      this.dialogTarget.querySelector("[data-slideover-content]") ||
      this.dialogTarget;

    // Check if we should use Turbo Frame lazy loading
    if (this.turboFrameSrcValue) {
      // Look for a turbo-frame in the container
      let turboFrame = container.querySelector("turbo-frame");

      if (!turboFrame) {
        // Create a turbo-frame if it doesn't exist
        turboFrame = document.createElement("turbo-frame");
        turboFrame.id = "slideover-lazy-content";

        // Clear any loading indicators or placeholder content
        container.innerHTML = "";
        container.appendChild(turboFrame);
      }

      // Set the src to trigger the lazy load
      turboFrame.src = this.turboFrameSrcValue;

      // Wait for the turbo-frame to load
      return new Promise((resolve) => {
        const handleLoad = () => {
          turboFrame.removeEventListener("turbo:frame-load", handleLoad);
          resolve();
        };

        turboFrame.addEventListener("turbo:frame-load", handleLoad);

        // Fallback timeout in case the frame doesn't load
        setTimeout(() => {
          turboFrame.removeEventListener("turbo:frame-load", handleLoad);
          resolve();
        }, 5000);
      });
    } else if (this.hasTemplateTarget) {
      // Use template-based lazy loading
      const templateContent = this.templateTarget.content.cloneNode(true);

      // Clear any loading indicators or placeholder content
      container.innerHTML = "";

      // Append the template content
      container.appendChild(templateContent);
    }
  }

  // Handle cleanup when dialog is closed by any means
  handleDialogClose() {
    // If the slideover was open, decrement the counter and restore title
    if (this.isOpen) {
      this.cleanupScrollbarCompensation();
      this.restoreTitle();
    }

    // Ensure the closing attribute is removed
    this.dialogTarget.removeAttribute("closing");
  }

  // Centralized method to handle scrollbar compensation cleanup
  cleanupScrollbarCompensation() {
    if (!this.isOpen) return;

    window.__openDialogCount = Math.max(0, window.__openDialogCount - 1);
    this.isOpen = false;

    // Only remove scrollbar compensation if no dialogs are open
    if (window.__openDialogCount === 0) {
      document.documentElement.style.removeProperty("--scrollbar-compensation");
      document.body.classList.remove("modal-open", "slideover-open");
    }
  }

  // ---------- NEW: Title Management Methods ----------

  /**
   * Update page title with slideover context
   * Looks for titleContext value or data-title-context attribute
   */
  updateTitle() {
    // Only update title if we have a context
    const context = this.hasTitleContextValue
      ? this.titleContextValue
      : this.element.dataset.titleContext;

    if (context) {
      // Store original title if not already stored
      if (!this.originalTitle) {
        this.originalTitle = document.title;
      }

      // Update title with context in parentheses
      document.title = `(${context}) ${this.originalTitle}`;
    }
  }

  /**
   * Restore the original page title
   */
  restoreTitle() {
    if (this.originalTitle) {
      document.title = this.originalTitle;
      this.originalTitle = null; // Clear stored title
    }
  }

  // ---------- Focus Trapping Methods ----------

  // Handle keydown events for focus trapping
  handleKeydown(event) {
    // Handle Tab key for focus trapping
    if (event.key === "Tab") {
      this.handleTabKey(event);
    }
  }

  // Set up focus trapping for the slideover
  setupFocusTrapping() {
    // Get all focusable elements within the slideover
    this.updateFocusableElements();

    // Check for elements with native autofocus attribute first
    const autofocusElement = this.dialogTarget.querySelector("[autofocus]");
    if (autofocusElement) {
      // Always respect native autofocus, regardless of device type
      autofocusElement.focus();
    } else if (this.autoFocusValue && this.firstFocusableElement) {
      // Fall back to auto-focusing first element if no native autofocus
      this.firstFocusableElement.focus();
    }
  }

  // Update the list of focusable elements
  updateFocusableElements() {
    const focusableSelector = [
      "a[href]",
      "area[href]",
      'input:not([disabled]):not([tabindex="-1"])',
      'button:not([disabled]):not([tabindex="-1"])',
      'textarea:not([disabled]):not([tabindex="-1"])',
      'select:not([disabled]):not([tabindex="-1"])',
      "details",
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable]:not([contenteditable="false"])',
    ].join(",");

    this.focusableElements = Array.from(
      this.dialogTarget.querySelectorAll(focusableSelector),
    ).filter((element) => {
      // Filter out elements that are not visible or have display: none
      return (
        element.offsetWidth > 0 &&
        element.offsetHeight > 0 &&
        getComputedStyle(element).display !== "none" &&
        getComputedStyle(element).visibility !== "hidden"
      );
    });

    this.firstFocusableElement = this.focusableElements[0] || null;
    this.lastFocusableElement =
      this.focusableElements[this.focusableElements.length - 1] || null;
  }

  // Handle Tab key for focus trapping
  handleTabKey(event) {
    // Update focusable elements in case the DOM has changed
    this.updateFocusableElements();

    if (this.focusableElements.length === 0) {
      // If no focusable elements, prevent tab and keep focus on dialog
      event.preventDefault();
      return;
    }

    if (this.focusableElements.length === 1) {
      // If only one focusable element, prevent tab and keep focus on it
      event.preventDefault();
      this.firstFocusableElement.focus();
      return;
    }

    // Handle normal tab navigation with wrapping
    if (event.shiftKey) {
      // Shift+Tab: moving backwards
      if (document.activeElement === this.firstFocusableElement) {
        event.preventDefault();
        this.lastFocusableElement.focus();
      }
    } else {
      // Tab: moving forwards
      if (document.activeElement === this.lastFocusableElement) {
        event.preventDefault();
        this.firstFocusableElement.focus();
      }
    }
  }
}
